/**
 * Users — read-only directory of the people at the viewer's own company.
 *
 * Suppliers see their organisation's users (across sibling sites in the same
 * group), customers see theirs. View only: no create/edit/delete, no
 * permission management. The admin management surface lives elsewhere
 * (/admin/users). Backed by GET /core/company-users/.
 */
import { useState, useMemo } from 'react';
import { coreAPI, UserProfile } from '../lib/api';
import { useFetchData } from '../hooks/useFetchData';
import { useFormatters } from '../contexts/PreferencesContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Loader2,
  AlertCircle,
  Search,
  LayoutGrid,
  List,
  Mail,
  Briefcase,
  Building2,
  Users as UsersIcon,
} from 'lucide-react';

type ViewMode = 'tile' | 'list';

/** Build initials from a user's name, falling back to the email. */
function initialsOf(profile: UserProfile): string {
  const first = profile.user.first_name?.trim() || '';
  const last = profile.user.last_name?.trim() || '';
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || first.charAt(0).toUpperCase();
  }
  return profile.user.email.charAt(0).toUpperCase();
}

/** Display name with sensible fallbacks. */
function nameOf(profile: UserProfile): string {
  const full = `${profile.user.first_name || ''} ${profile.user.last_name || ''}`.trim();
  return full || profile.user.email;
}

/** Deterministic accent colour from a string so avatars are visually varied. */
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-indigo-100 text-indigo-700',
];
function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function Avatar({ profile, size = 'md' }: { profile: UserProfile; size?: 'md' | 'lg' }) {
  const dims = size === 'lg' ? 'h-12 w-12 text-base' : 'h-9 w-9 text-sm';
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${dims} ${avatarColor(
        profile.user.email,
      )}`}
    >
      {initialsOf(profile)}
    </div>
  );
}

export default function Users() {
  const { formatDate } = useFormatters();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewMode>('tile');

  const { data, loading, error } = useFetchData(
    () => coreAPI.getCompanyDirectory({ page_size: 500 }).then((res) => res.data.results),
  );

  const users = useMemo(() => data ?? [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((p) => {
      const haystack = [
        nameOf(p),
        p.user.email,
        p.role_name,
        p.job_title,
        p.department,
        ...(p.site_names || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [users, search]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <UsersIcon className="h-6 w-6 text-gray-500" />
            Users
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            People at your organisation. View only.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, role…"
              className="w-64 pl-9"
            />
          </div>
          <div className="flex items-center rounded-md border border-gray-200 p-0.5">
            <button
              type="button"
              onClick={() => setView('tile')}
              aria-label="Tile view"
              className={`rounded p-1.5 transition-colors ${
                view === 'tile' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-label="List view"
              className={`rounded p-1.5 transition-colors ${
                view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* States */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-500">
          <Loader2 className="mr-2 h-6 w-6 animate-spin" />
          Loading users…
        </div>
      ) : error ? (
        <div className="flex items-center justify-center gap-2 rounded-md border border-red-200 bg-red-50 py-12 text-red-700">
          <AlertCircle className="h-5 w-5" />
          Could not load users.
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-24 text-gray-400">
          <UsersIcon className="h-10 w-10" />
          <p className="text-sm">{search ? 'No users match your search.' : 'No users found.'}</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500">
            {filtered.length} {filtered.length === 1 ? 'user' : 'users'}
          </p>
          {view === 'tile' ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((p) => (
                <Card key={p.id} className="transition-shadow hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <Avatar profile={p} size="lg" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-gray-900" title={nameOf(p)}>
                          {nameOf(p)}
                        </p>
                        {p.role_name && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {p.role_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 space-y-2 text-sm text-gray-600">
                      <a
                        href={`mailto:${p.user.email}`}
                        className="flex items-center gap-2 truncate text-gray-600 hover:text-gray-900"
                        title={p.user.email}
                      >
                        <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                        <span className="truncate">{p.user.email}</span>
                      </a>
                      {p.job_title && (
                        <div className="flex items-center gap-2 truncate" title={p.job_title}>
                          <Briefcase className="h-4 w-4 shrink-0 text-gray-400" />
                          <span className="truncate">
                            {p.job_title}
                            {p.department ? ` · ${p.department}` : ''}
                          </span>
                        </div>
                      )}
                      {p.site_names && p.site_names.length > 0 && (
                        <div className="flex items-start gap-2">
                          <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                          <span className="text-gray-600">{p.site_names.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Job Title</TableHead>
                      <TableHead>Sites</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar profile={p} />
                            <span className="font-medium text-gray-900">{nameOf(p)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <a
                            href={`mailto:${p.user.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {p.user.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          {p.role_name ? (
                            <Badge variant="secondary" className="text-xs">
                              {p.role_name}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {p.job_title || <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {p.site_names && p.site_names.length > 0 ? (
                            p.site_names.join(', ')
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-600">
                          {p.created_at ? formatDate(p.created_at) : <span className="text-gray-400">—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
